import { ArrowsLeftRight } from '@phosphor-icons/react';
import React from 'react';
import { Link } from 'react-router';

import { getStockTransferLines } from '@api/inventory';
import type { StockTransferLine } from '@api/inventory';
import type { DataSheetColumn } from '@components/ui/DataSheet';
import { formatCount } from '@utils/currency';

import TransferLinesModal from './TransferLinesModal';

/** Passes open longer than this are flagged so someone closes them in the WMS. Still counted. */
const OLD_PASS_DAYS = 30;

interface StockTransferModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: string;
    skuLabel?: string;
    /** Narrow to transfers into one internal warehouse (Internal pivot warehouse column). */
    warehouseName?: string;
}

const daysOpen = (dispatchedAt: string | null): number | null =>
    dispatchedAt
        ? Math.max(0, Math.floor((Date.now() - new Date(dispatchedAt).getTime()) / 86_400_000))
        : null;

/**
 * The open stock transfers behind a Stock Transfer cell: each gate pass / STN moving this SKU
 * between two of the brand's own warehouses, with how long it has been open. Served by the same
 * backend function that computed the cell, so the rows always add up to the number clicked.
 * Each code links to its Stock Transfers page.
 */
const StockTransferModal: React.FC<StockTransferModalProps> = ({
    isOpen,
    onClose,
    productId,
    skuLabel,
    warehouseName,
}) => {
    const [lines, setLines] = React.useState<StockTransferLine[] | null>(null);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getStockTransferLines({ productId, warehouseName })
            .then((res) => {
                setLines(res.data ?? []);
                setTotal(res.total ?? 0);
            })
            .catch(() => setLines(null))
            .finally(() => setLoading(false));
    }, [isOpen, productId, warehouseName]);

    const columns: DataSheetColumn<StockTransferLine>[] = [
        {
            key: 'code',
            header: 'Gate pass / STN',
            sticky: true,
            width: 210,
            render: (_value, row) => {
                const age = daysOpen(row.dispatchedAt);
                return (
                    <div>
                        <Link
                            to={`/stock-transfers/${row.stnId}`}
                            className="break-all font-mono text-xs text-blue-600 hover:underline"
                        >
                            {row.code}
                        </Link>
                        {age !== null && age > OLD_PASS_DAYS && (
                            <div className="mt-0.5 text-[11px] font-semibold text-amber-600">
                                Open {age} days · check in Unicommerce
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'route',
            header: 'From → To',
            width: 220,
            render: (_value, row) => `${row.fromWarehouse ?? '—'} → ${row.toWarehouse ?? '—'}`,
            csvValue: (_value, row) => `${row.fromWarehouse ?? ''} → ${row.toWarehouse ?? ''}`,
        },
        {
            key: 'dispatchedAt',
            header: 'Dispatched',
            width: 110,
            render: (value) =>
                value
                    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                    : '—',
        },
        {
            key: 'dispatchedQty',
            header: 'Sent',
            align: 'right',
            width: 80,
            render: (value) => formatCount(value ?? 0),
        },
        {
            key: 'receivedQty',
            header: 'Received',
            align: 'right',
            width: 100,
            render: (value) => formatCount(value ?? 0),
        },
        {
            key: 'openQty',
            header: 'On transfer',
            align: 'right',
            width: 110,
            render: (value) => <span className="font-semibold">{formatCount(value ?? 0)}</span>,
        },
    ];

    return (
        <TransferLinesModal
            isOpen={isOpen}
            onClose={onClose}
            icon={<ArrowsLeftRight size={18} weight="bold" aria-hidden />}
            title="Stock transfers between your warehouses"
            subtitle={
                <>
                    {skuLabel ?? productId}
                    {warehouseName ? ` · into ${warehouseName}` : ''}
                    {lines
                        ? ` · ${formatCount(total)} units on ${lines.length} transfer${lines.length === 1 ? '' : 's'}`
                        : ''}
                </>
            }
            loading={loading}
            lines={lines}
            columns={columns}
            getRowKey={(line) => `${line.stnId}`}
            emptyText="Nothing is on a transfer for this SKU right now"
            footerNote="Dispatched from one of your warehouses and not yet received at the other. Counted in stock and DoH."
            footerLink={{ to: '/stock-transfers', label: 'Open Stock Transfers →' }}
        />
    );
};

export default StockTransferModal;
